import { Pad, PadBox } from 'component/basics';
import { TextButton } from 'component/button';
import { colorSurfaceHighlight, colorTextPrimary, colorTextSecondary } from 'component/color';
import { CommentsInput } from 'component/comment';
import { spacings } from 'component/constants';
import { formatToDateRangeString } from 'component/date';
import { Byline } from 'component/people';
import { Paragraph } from 'component/text';
import { useClientLocale } from 'component/translation';
import { CommunityGuidelines } from 'feature/question/community-guidelines';
import { AskMeAnythingLiveStatus, TopicDate } from 'open-modules/cbcrc/component/header';
import React from 'react';
import { View } from 'react-native';
import type { TopicConfig } from 'structure/topic';
import { endAtProperty, startAtProperty } from 'structure/topic';
import { useIsAdmin } from 'util/admin';
import { useDatastore, useGlobalProperty } from 'util/datastore';
import { useConfig } from 'util/features';
import { fromProperty, t } from 'util/stdtypes';
import { toBool } from 'util/util';

import { PublicationStatusAdminScreen } from './live-status';

export const AskMeAnythingHeaderFeature = {
    key: 'amaheader',
    name: 'Ask me anything header',
    dev: true,
    subscreens: {
        publicationStatusAdmin: () => <PublicationStatusAdminScreen />,
    },
    providesConfig: {
        topicHeaderRightWidgets: [],
        topicHeaderBottomWidgets: [AskMeAnythingHeaderByline, AskMeAnythingHeaderBottomWidget],
        headerBackgroundColor: colorSurfaceHighlight,
        headerFontColor: colorTextPrimary,
        topicTopWidgets: [AskMeAnythingTopWidget],
    },
};

export function AskMeAnythingLiveStatusWidget() {
    const datastore = useDatastore();
    const endAt = useGlobalProperty(endAtProperty);
    const isAdmin = useIsAdmin();
    const live = toBool(endAt && endAt > new Date().toISOString());

    return (
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <AskMeAnythingLiveStatus live={live} />
            {isAdmin && (
                <TextButton
                    color={colorTextSecondary}
                    label={t('Edit live status (admin)')}
                    onPress={() => datastore.pushSubscreen('publicationStatusAdmin')}
                    type={'tiny'}
                />
            )}
        </View>
    );
}

export function AskMeAnythingStatusDescription() {
    return null;
}

function AskMeAnythingHeaderByline() {
    const from = useGlobalProperty(fromProperty);
    if (!from) return null;
    return <Byline userId={from} />;
}

function AskMeAnythingHeaderBottomWidget() {
    const { readOnly, composerPreview } = useConfig<TopicConfig>();

    if (readOnly && composerPreview) {
        return (
            <PadBox bottom={spacings.sm} top={spacings.xl}>
                {React.createElement(composerPreview)}
            </PadBox>
        );
    } else {
        return null;
    }
}

export function AskMeAnythingTopWidget() {
    const startAt = useGlobalProperty(startAtProperty);
    const endAt = useGlobalProperty(endAtProperty);
    const locale = useClientLocale();
    const { readOnly } = useConfig<TopicConfig>();
    const live = toBool(endAt && endAt > new Date().toISOString());

    if (readOnly) {
        return null;
    }

    if (live) {
        return (
            <PadBox bottom={spacings.sm} horiz={spacings.lg} top={spacings.xl}>
                <CommentsInput />
                <Pad size={spacings.sm} />
            </PadBox>
        );
    } else if (startAt && endAt) {
        return (
            <PadBox bottom={spacings.sm} vert={spacings.xl}>
                <TopicDate date={formatToDateRangeString({ start: new Date(startAt), end: new Date(endAt), locale })} />
            </PadBox>
        );
    } else {
        return null;
    }
}
